import { cloneDeep } from 'lodash';
import React from 'react';
import { Select } from '@meitu/saas-ui';
import { Editor } from '@meitu/saas-editor-core/es/index';
import { IImageEffectShadowType, IImageLayerProps, LayerType } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import LabelAction from '@/components/label-action';
import { useLocaleStore } from '@/i18n/index';
import { useActiveData } from '../../active-data-context';
import EffectItem, { EffectItemShowTitle } from '../../components/effect-item/effect-item';
import {
    getDefaultLightProjectionShadowValue,
    getDefaultReflectionShadowValue,
    getDefaultShadowValue,
    getDefaultWaterSurfaceShadowValue,
    getShadowTypeOptions,
} from '../../constant';
import BaseShadowItem from './base-shadow-item';
import LightProjectionShadowItem from './light-projection-shadow-item';
import ReflectionShadowItem from './reflection-shadow-item';
import WaterSurfaceShadowItem from './water-surface-shadow-item';

const ImageShadowItem: React.FC<{
    funcStatTrack: Function;
    editor: Editor;
}> = ({ funcStatTrack }) => {
    const { $t } = useLocaleStore();
    const { activeLayer, setActiveData } = useActiveData();
    if (!activeLayer || activeLayer.layerType !== LayerType.IMAGE_LAYER) return null;

    const imageData = activeLayer.data as IImageLayerProps;
    const effect = imageData.effect;
    const doFuncStatTrack = () => {
        funcStatTrack({
            useId: 20,
            downloadId: 20,
            clickId: 49,
        });
    };
    const addShadow = () => {
        if (!imageData.effect) {
            imageData.effect = {
                effectAlpha: 1,
                glow: [],
                shadow: [getDefaultShadowValue()],
                stroke: [],
                screenshotUrl: '',
            };
        } else {
            imageData.effect.shadow = [getDefaultShadowValue()];
        }
        const newLayer = cloneDeep(activeLayer);
        setActiveData(newLayer, { statTrackKey: 'effectShadow' });
        doFuncStatTrack();
    };

    if (!effect || !effect.shadow || effect.shadow.length === 0) {
        return (
            <EffectItem
                title={$t((v) => v.imageEffectPanel.imageShadowItem.shadow)}
                iconName="DropshadowBold"
                handleClick={() => {
                    addShadow();
                }}
            />
        );
    }

    const shadow = effect.shadow[0];
    shadow.type = shadow.type || IImageEffectShadowType.base;

    const updateData = (data: { key: string; value: any }[], isEnd = false, stopUpdate = false) => {
        if (isEnd) {
            doFuncStatTrack();
        }
        if (stopUpdate || !imageData.effect) {
            return;
        }
        data.forEach((item) => {
            if (imageData.effect) {
                imageData.effect.shadow[0][item.key] = item.value;
            }
        });
        const newLayer = cloneDeep(activeLayer);
        setActiveData(newLayer, { statTrackKey: 'effectShadow' });
    };

    const visibleClick = () => {
        const originVisible = shadow.hidden;
        updateData([{ key: 'hidden', value: !originVisible }], true, false);
    };

    const delClick = () => {
        if (!imageData.effect) {
            return;
        }
        imageData.effect.shadow = [];
        const newLayer = cloneDeep(activeLayer);
        setActiveData(newLayer, { statTrackKey: 'effectShadow' });
        doFuncStatTrack();
    };

    const shadowTypeChange = (value: IImageEffectShadowType) => {
        doFuncStatTrack();
        if (!imageData.effect) {
            return;
        }
        if (value === IImageEffectShadowType.lightProjection) {
            imageData.effect.shadow[0] = getDefaultLightProjectionShadowValue();
        } else if (value === IImageEffectShadowType.reflection) {
            imageData.effect.shadow[0] = getDefaultReflectionShadowValue();
        } else if (value === IImageEffectShadowType.waterSurface) {
            imageData.effect.shadow[0] = getDefaultWaterSurfaceShadowValue();
        } else {
            imageData.effect.shadow[0] = getDefaultShadowValue();
        }
        const newLayer = cloneDeep(activeLayer);
        setActiveData(newLayer, { statTrackKey: 'effectShadow' });
    };

    return (
        <div className="c-image-effect-item-content">
            <EffectItemShowTitle
                title={$t((v) => v.imageEffectPanel.imageShadowItem.shadow)}
                iconName="DropshadowBold"
                handleVisibleClick={visibleClick}
                handleDelClick={delClick}
                visible={!shadow.hidden}
            />

            <div className="content">
                <LabelAction
                    title={$t((v) => v.imageEffectPanel.imageStrokeItem.style)}
                    align
                    style={{ marginBottom: '14px' }}
                >
                    <div
                        style={{
                            width: '100%',
                        }}
                    >
                        <Select
                            style={{ width: '100%' }}
                            value={shadow.type || IImageEffectShadowType.base}
                            onChange={shadowTypeChange}
                            popupClassName="c-image-stroke-item-select"
                            suffixIcon={<IconFont name="ChevronDownBold" />}
                        >
                            {getShadowTypeOptions().map((item) => {
                                return (
                                    <Select.Option value={item.value} key={item.value}>
                                        {item.label}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </div>
                </LabelAction>
                {shadow.type === IImageEffectShadowType.base && (
                    <BaseShadowItem shadow={shadow} updateData={updateData} />
                )}

                {shadow.type === IImageEffectShadowType.lightProjection && (
                    <LightProjectionShadowItem shadow={shadow} updateData={updateData} />
                )}

                {shadow.type === IImageEffectShadowType.reflection && (
                    <ReflectionShadowItem shadow={shadow} updateData={updateData} />
                )}

                {shadow.type === IImageEffectShadowType.waterSurface && (
                    <WaterSurfaceShadowItem shadow={shadow} updateData={updateData} />
                )}
            </div>
        </div>
    );
};

export default ImageShadowItem;
